import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import bestLogo from '../assets/logo-best.jpeg';
import './Login.css';

const roles = [
  { id: 'student', label: 'Pelajar', shortLabel: 'Pelajar', color: '#4c7cf0' },
  { id: 'teacher', label: 'Guru IVC', shortLabel: 'Guru', color: '#2f9d66' },
  { id: 'coach', label: 'Jurulatih Play & Talk', shortLabel: 'Jurulatih', color: '#e59a2e' },
  { id: 'admin', label: 'Pentadbir', shortLabel: 'Pentadbir', color: '#9c4dcc' },
];

const dashboardRoutes = {
  student: '/dashboard/student',
  teacher: '/dashboard/teacher',
  coach: '/dashboard/coach',
  admin: '/dashboard/admin',
};

const ADMIN_ALLOWED_EMAILS = ['aleyannazifa@gmail.com', 'btopmalaysia@gmail.com'];

const Login = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    selectedRole: 'student',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const roleMeta = roles.find((role) => role.id === formData.selectedRole);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleRoleSelect = (roleId) => {
    setFormData((prev) => ({ ...prev, selectedRole: roleId }));
    setError('');
  };

  const redirectToDashboard = (role) => {
    navigate(dashboardRoutes[role] || '/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('Profil pengguna tidak dijumpai. Sila hubungi pentadbir.');
      }

      const userData = userDoc.data();
      const actualRole = userData.role;
      const status = userData.status;
      const userEmail = user.email?.toLowerCase() || '';

      if (status !== 'active') {
        setError('Akaun anda tidak aktif. Sila hubungi pentadbir.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (actualRole === 'admin' && !ADMIN_ALLOWED_EMAILS.includes(userEmail)) {
        setError('Akaun ini tidak dibenarkan mengakses papan pemuka pentadbir.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (formData.selectedRole !== actualRole) {
        const chosen = roles.find((role) => role.id === formData.selectedRole)?.label;
        const actual = roles.find((role) => role.id === actualRole)?.label;

        setError(
          `Anda pilih "${chosen}" tetapi akaun anda adalah "${actual}". Mengalihkan ke papan pemuka yang betul...`
        );
        setTimeout(() => redirectToDashboard(actualRole), 1600);
        return;
      }

      redirectToDashboard(actualRole);
    } catch (err) {
      console.error('Login error:', err);

      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('E-mel atau kata laluan tidak sah.');
          break;
        case 'auth/invalid-email':
          setError('Format e-mel tidak sah.');
          break;
        case 'auth/too-many-requests':
          setError('Terlalu banyak percubaan. Sila cuba lagi sebentar.');
          break;
        default:
          setError(err.message || 'Ralat berlaku. Sila cuba lagi.');
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Sila masukkan e-mel anda terlebih dahulu.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, formData.email);
      setResetEmailSent(true);
      setError('');
      setTimeout(() => setResetEmailSent(false), 5000);
    } catch (err) {
      console.error(err);
      setError('Ralat menghantar e-mel. Sila semak e-mel anda.');
    }
  };

  return (
    <div className="login-shell">
      <div className="login-backdrop" />
      <button type="button" className="auth-home-link" onClick={() => navigate('/')} aria-label="Laman Utama">
        <span aria-hidden="true" />
      </button>

      <main className="login-page">
        <section className="login-promo" aria-label="Pengenalan BTOP Wellness">
          <div className="promo-panel">
            <div className="promo-eyebrow">PROGRAM BEST 2.0</div>
            <h1 className="promo-title">Log masuk untuk pantau program dengan mudah.</h1>
            <p className="promo-copy">
              Akses jadual, kehadiran, refleksi kelas dan maklumat program mengikut
              peranan anda dalam portal BEST 2.0.
            </p>

            <div className="promo-points">
              <div className="promo-point">
                <span className="promo-point-icon promo-point-qr" aria-hidden="true"><span /></span>
                <span>Rekod kehadiran ikut sesi</span>
              </div>
              <div className="promo-point">
                <span className="promo-point-icon promo-point-calendar" aria-hidden="true"><span /></span>
                <span>Jadual ikut sekolah, daerah, dan terminal</span>
              </div>
              <div className="promo-point">
                <span className="promo-point-icon promo-point-roles" aria-hidden="true"><span /></span>
                <span>Paparan khusus untuk pelajar, guru, jurulatih dan pentadbir</span>
              </div>
            </div>

            <div className="promo-dashboard-preview" aria-hidden="true">
              <div className="preview-head">
                <span>Paparan Langsung</span>
                <strong>84%</strong>
              </div>
              <div className="preview-bars">
                <i style={{ '--height': '52%' }} />
                <i style={{ '--height': '76%' }} />
                <i style={{ '--height': '64%' }} />
                <i style={{ '--height': '86%' }} />
              </div>
              <div className="preview-row">
                <span>Sesi seterusnya</span>
                <strong>BI / Terminal 2</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="login-card" aria-label="Borang log masuk">
          <div className="brand-row">
            <div className="brand-mark">
              <img src={bestLogo} alt="Logo BEST BTOP" />
            </div>
            <div className="brand-copy">
              <div className="brand-name">BTOP Wellness</div>
              <div className="brand-subtitle">Program BEST 2.0</div>
            </div>
          </div>

          <div className="welcome-block">
            <h2>Selamat Datang</h2>
            <p>Log masuk untuk meneruskan akses ke papan pemuka anda.</p>
          </div>

          <div className="role-section">
            <div className="section-label">Log Masuk Sebagai</div>

            <div className="role-grid">
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  className={`role-chip ${formData.selectedRole === role.id ? 'active' : ''}`}
                  onClick={() => handleRoleSelect(role.id)}
                  style={{ '--role-color': role.color }}
                >
                  <span className={`role-chip-icon role-chip-icon-${role.id}`} aria-hidden="true"><span /></span>
                  <span className="role-chip-text">{role.shortLabel}</span>
                </button>
              ))}
            </div>

            <div className="role-current" style={{ '--role-color': roleMeta?.color }}>
              <span className="role-current-dot" />
              <span>{roleMeta?.label}</span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="field-group">
              <label htmlFor="email">E-mel</label>
              <div className="input-shell">
                <span className="input-icon" aria-hidden="true">@</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="nama@contoh.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="field-group">
              <div className="password-row">
                <label htmlFor="password">Kata Laluan</label>
                <button type="button" className="text-link inline-link" onClick={handleForgotPassword}>
                  Lupa kata laluan?
                </button>
              </div>

              <div className="input-shell">
                <span className="input-icon input-icon-lock" aria-hidden="true" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Masukkan kata laluan"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggle-button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Sembunyikan kata laluan' : 'Tunjuk kata laluan'}
                >
                  {showPassword ? 'Sembunyi' : 'Tunjuk'}
                </button>
              </div>
            </div>

            {error && (
              <div className="feedback error" role="alert">
                <span className="feedback-icon">!</span>
                <span>{error}</span>
              </div>
            )}

            {resetEmailSent && (
              <div className="feedback success" role="status">
                <span className="feedback-icon feedback-icon-check" aria-hidden="true" />
                <span>Pautan tetapan semula kata laluan telah dihantar ke e-mel anda.</span>
              </div>
            )}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" />
                  <span>Memuatkan...</span>
                </>
              ) : (
                <>
                  <span>Log Masuk</span>
                  <span className="submit-arrow">-&gt;</span>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <span>Belum ada akaun?</span>
            <button type="button" className="text-link" onClick={() => navigate('/signup')}>
              Daftar akaun baharu
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Login;
